import { z } from 'zod';

export const playerSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  photoUrl: z.string().nullable(),
  telegramUserId: z.number().nullable(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  isNew: z.boolean(),
});
export type Player = z.infer<typeof playerSchema>;

export const otpRequestSchema = z.object({ devCode: z.string().nullable() });

export const notificationPrefsSchema = z.object({
  push: z.boolean(),
  tournaments: z.boolean(),
  rating: z.boolean(),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

export const authResponseSchema = z.object({
  token: z.string(),
  player: playerSchema,
});

export const seasonSchema = z.object({
  id: z.number(),
  name: z.string(),
  /** Every 10 seasons open a new chapter (absolute season 11 = chapter 2, season 1). */
  chapter: z.number(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  finaleAt: z.string().nullable(),
  isActive: z.boolean(),
  tournaments: z.number(),
  players: z.number(),
});
export type Season = z.infer<typeof seasonSchema>;

/* ── Зал славы: чемпионы сезонов ── */

export const championBriefSchema = z.object({
  playerId: z.number(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  points: z.number(),
  wins: z.number(),
  tournaments: z.number(),
});
export type ChampionBrief = z.infer<typeof championBriefSchema>;

export const hallSeasonSchema = z.object({
  seasonId: z.number(),
  chapter: z.number(),
  status: z.enum(['finished', 'active']),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  finaleAt: z.string().nullable(),
  /** null: сезон активен или чемпион ещё не назначен. */
  champion: championBriefSchema.nullable(),
});
export type HallSeason = z.infer<typeof hallSeasonSchema>;

export const championTop3RowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  points: z.number(),
});

export const championCardSchema = z.object({
  seasonId: z.number(),
  chapter: z.number(),
  status: z.literal('finished'),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  champion: championBriefSchema.extend({
    title: z.string().nullable(),
    quote: z.string().nullable(),
    reward: z.string().nullable(),
  }),
  top3: z.array(championTop3RowSchema),
});
export type ChampionCard = z.infer<typeof championCardSchema>;

/** Надетая косметика Straddle Pass (в строках списков и профилях). */
const cosmeticItemSchema = z.object({
  code: z.string(),
  kind: z.string(),
  asset: z.string(),
  rarity: z.number(),
});
export const cosmeticsSchema = z.object({
  frame: cosmeticItemSchema.nullable(),
  nick: cosmeticItemSchema.nullable(),
  row: cosmeticItemSchema.nullable(),
  bg: cosmeticItemSchema.nullable(),
});
export type Cosmetics = z.infer<typeof cosmeticsSchema>;
export type CosmeticItem = z.infer<typeof cosmeticItemSchema>;

export const leaderboardRowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  avatarUrl: z.string().nullable().optional(),
  points: z.number(),
  wins: z.number(),
  gamesPlayed: z.number(),
  isMe: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
});
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;

export const leaderboardSchema = z.object({
  seasonId: z.number().nullable(),
  rows: z.array(leaderboardRowSchema),
  me: leaderboardRowSchema.nullable(),
});
export type Leaderboard = z.infer<typeof leaderboardSchema>;

export const recentTournamentSchema = z.object({
  gameId: z.number(),
  startTime: z.string(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  position: z.number().nullable(),
  points: z.number(),
});

export const myTournamentSchema = recentTournamentSchema.extend({
  fieldSize: z.number(),
  seasonId: z.number().nullable(),
  /** Рейтинговая игра; false у финалов/спец-турниров — вне знаменателя «В очках %». */
  rated: z.boolean().optional().default(false),
});
export type MyTournament = z.infer<typeof myTournamentSchema>;

export const tournamentResultSchema = z.object({
  game: z.object({
    id: z.number(),
    startTime: z.string(),
    title: z.string().nullable(),
    format: z.string().nullable(),
    seasonId: z.number().nullable(),
    fieldSize: z.number(),
  }),
  rows: z.array(
    z.object({
      position: z.number().nullable(),
      playerId: z.number(),
      name: z.string(),
      avatarUrl: z.string().nullable().optional(),
      points: z.number(),
      /** Разбивка очков (место/баунти); null — игра ещё не пересчитана движком. */
      placePoints: z.number().nullable().optional(),
      bountyPoints: z.number().nullable().optional(),
      isMe: z.boolean(),
      cosmetics: cosmeticsSchema.optional(),
    }),
  ),
  pass: z
    .object({
      deckId: z.number(),
      rarity: z.number(),
      opened: z.boolean(),
      levelsGained: z.number(),
      /** XP этой игры (база + задания); levelsGained = xpGained/100. */
      xpGained: z.number().optional(),
      /** Апгрейд за финальный стол + состояние пасса для полосы (6.3). */
      upgraded: z.boolean().optional(),
      level: z.number().nullable().optional(),
      xpFrac: z.number().nullable().optional(),
      guaranteeIn: z.number().nullable().optional(),
      questsDone: z.array(z.object({ title: z.string(), reward: z.number() })).optional(),
    })
    .nullable()
    .optional(),
});
export type TournamentResult = z.infer<typeof tournamentResultSchema>;

export const profileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  nickname: z.string().nullable(),
  email: z.string().nullable(),
  photoUrl: z.string().nullable(),
  termsAccepted: z.boolean(),
  onboardingCompleted: z.boolean(),
  /** Онбординг-сторис просмотрен (optional: совместимость со старым API). */
  introSeen: z.boolean().optional(),
  notifications: notificationPrefsSchema,
  cosmetics: cosmeticsSchema.nullable().optional(),
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type Profile = z.infer<typeof profileSchema>;

export const publicProfileSchema = z.object({
  id: z.number(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  cosmetics: cosmeticsSchema.nullable().optional(),
  seasonId: z.number().nullable(),
  stats: z.object({
    points: z.number(),
    rank: z.number().nullable(),
    wins: z.number(),
    gamesPlayed: z.number(),
    itmPercent: z.number(),
  }),
  recent: z.array(recentTournamentSchema),
});
export type PublicProfile = z.infer<typeof publicProfileSchema>;

export const livePlayerSchema = z.object({
  playerId: z.number().nullable(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  status: z.string(),
  stack: z.number(),
  tableNo: z.number().nullable(),
  rebuys: z.number(),
  finalPosition: z.number().nullable(),
  knockouts: z.number(),
  /** Provisional rating points earned so far (place points + bounty). */
  points: z.number(),
  isMe: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
});
export type LivePlayer = z.infer<typeof livePlayerSchema>;

export const liveSchema = z.object({
  live: z.boolean(),
  status: z.string(),
  updatedAt: z.string().nullable(),
  level: z.number(),
  smallBlind: z.number(),
  bigBlind: z.number(),
  ante: z.number(),
  isBreak: z.boolean(),
  isPaused: z.boolean(),
  levelEndsAt: z.string().nullable(),
  timeLeftSec: z.number(),
  /** Late registration: open until the END of the flagged blind level in Korin. */
  lateRegOpen: z.boolean(),
  lateRegTimeLeftSec: z.number(),
  lateRegEndsAt: z.string().nullable(),
  lateRegLevel: z.number().nullable(),
  /** Arrived players (IN_GAME + OUT), like Korin's display. */
  playersTotal: z.number(),
  playersIn: z.number(),
  totalRebuys: z.number(),
  knockouts: z.number(),
  startingStack: z.number(),
  avgStack: z.number(),
  chipsInPlay: z.number(),
  breakInSec: z.number(),
  breakEndsAt: z.string().nullable(),
  nextSmallBlind: z.number(),
  nextBigBlind: z.number(),
  /** Сетка призовых мест (место → провизорные очки); default([]) — совместимость
   *  со старыми кэшами/пейлоадами без поля. */
  prizes: z.array(z.object({ place: z.number(), points: z.number() })).default([]),
  players: z.array(livePlayerSchema),
});
export type Live = z.infer<typeof liveSchema>;

export const appStatusSchema = z.object({
  maintenance: z.boolean(),
  message: z.string().nullable(),
});
export type AppStatus = z.infer<typeof appStatusSchema>;

export const avatarUploadSchema = z.object({ photoUrl: z.string() });

export const tournamentSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  dayOfWeek: z.string().nullable(),
  title: z.string().nullable(),
  format: z.string().nullable(),
  buyIn: z.number(),
  guarantee: z.number().nullable(),
  startingStack: z.number().nullable(),
  blindMinutes: z.number().nullable(),
  description: z.string().nullable(),
  status: z.string().nullable(),
  seasonId: z.number().nullable(),
  capacity: z.number().nullable(),
  registrationOpen: z.boolean(),
  registeredCount: z.number(),
  registeredByMe: z.boolean(),
  waitlistCount: z.number(),
  myWaitlisted: z.boolean(),
  myWaitlistPosition: z.number().nullable(),
  /** Past-list extras (T1 cards); null for upcoming tournaments. */
  playersCount: z.number().nullable(),
  winner: z.string().nullable(),
  myPlace: z.number().nullable(),
  myPoints: z.number().nullable(),
  /** Straddle Pass: моё задание на эту игру (detail, если записан). */
  myPassQuest: z
    .object({
      title: z.string(),
      difficulty: z.enum(['easy', 'medium', 'hard']).nullable(),
      reward: z.number(),
      status: z.string(),
    })
    .optional(),
});
export type Tournament = z.infer<typeof tournamentSchema>;

export const registrationSchema = z.object({
  playerId: z.number(),
  nickname: z.string(),
  photoUrl: z.string().nullable(),
  registeredAt: z.string(),
  rank: z.number().nullable(),
  waitlisted: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
});
export type Registration = z.infer<typeof registrationSchema>;

export const registrationsSchema = z.object({
  players: z.array(registrationSchema),
  total: z.number(),
});
export type Registrations = z.infer<typeof registrationsSchema>;

export const newsSchema = z.object({
  id: z.number(),
  title: z.string(),
  body: z.string().nullable(),
  icon: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isPinned: z.boolean(),
  publishedAt: z.string(),
});
export type NewsItem = z.infer<typeof newsSchema>;

export const homeSchema = z.object({
  nextTournament: tournamentSchema.nullable(),
  news: z.array(newsSchema),
});
export type Home = z.infer<typeof homeSchema>;

/* ── Достижения (design_handoff_achievements) ── */

export const seasonalStateSchema = z.enum(['active', 'fixed', 'missed']);
export type SeasonalState = z.infer<typeof seasonalStateSchema>;

export const achievementRowSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  secret: z.boolean(),
  masked: z.boolean(),
  kind: z.enum(['permanent', 'seasonal']),
  seasonId: z.number().nullable(),
  state: seasonalStateSchema.nullable(),
  level: z.number(),
  maxLevel: z.number(),
  progress: z.number().nullable(),
  nextThreshold: z.number().nullable(),
  higherIsBetter: z.boolean(),
  paused: z.boolean(),
  earnedPoints: z.number(),
  totalPoints: z.number(),
  unlockedAt: z.string().nullable(),
});
export type AchievementRow = z.infer<typeof achievementRowSchema>;

export const ladderStepSchema = z.object({
  n: z.number(),
  title: z.string().nullable(),
  threshold: z.number(),
  points: z.number(),
  unlockedAt: z.string().nullable(),
});

export const achievementDetailSchema = achievementRowSchema.extend({
  ladder: z.array(ladderStepSchema),
  daysLeft: z.number().nullable(),
  bySeasons: z.array(
    z.object({ seasonId: z.number(), state: seasonalStateSchema.nullable(), level: z.number() }),
  ),
  pinned: z.boolean(),
  /** Ключ метрики (MA2: спец-блок пика личного рейтинга). */
  metric: z.string().optional(),
  /** Достижения отбора: номер отбора + hint (SA3). */
  selection: z
    .object({ ordinal: z.number(), hint: z.string().nullable() })
    .nullable()
    .optional(),
});
export type AchievementDetail = z.infer<typeof achievementDetailSchema>;

export const showcaseItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  icon: z.string(),
  level: z.number(),
  maxLevel: z.number(),
  kind: z.enum(['permanent', 'seasonal']),
  state: seasonalStateSchema.nullable(),
});
export type ShowcaseItem = z.infer<typeof showcaseItemSchema>;

export const achievementsSummarySchema = z.object({
  isOwner: z.boolean(),
  points: z.number(),
  loyalty: z
    .object({
      name: z.string(),
      discountPct: z.number(),
      icon: z.string().nullable(),
      next: z.object({ name: z.string(), threshold: z.number() }).nullable(),
    })
    .nullable(),
  doneCount: z.number(),
  totalCount: z.number(),
  recent: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      icon: z.string().nullable(),
      masked: z.boolean(),
      level: z.number(),
      at: z.string(),
    }),
  ),
  categories: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      done: z.number(),
      total: z.number(),
      seasonal: z.boolean(),
    }),
  ),
  seasonal: z
    .object({
      seasonId: z.number(),
      daysLeft: z.number().nullable(),
      done: z.number(),
      total: z.number(),
    })
    .nullable(),
  showcase: z.array(showcaseItemSchema),
});
export type AchievementsSummary = z.infer<typeof achievementsSummarySchema>;

export const achievementCategorySchema = z.object({
  id: z.union([z.number(), z.literal('seasonal')]),
  name: z.string(),
  rows: z.array(achievementRowSchema),
});
export type AchievementCategory = z.infer<typeof achievementCategorySchema>;

export const celebrationSchema = z.object({
  achievementId: z.number(),
  /** Код определения — выбор варианта листа (M05 → легендарный, sel_* → отбор). */
  code: z.string().optional(),
  name: z.string(),
  icon: z.string(),
  level: z.number(),
  levelTitle: z.string().nullable(),
  points: z.number(),
  maxLevel: z.number(),
});
export type Celebration = z.infer<typeof celebrationSchema>;

/* ── Личный рейтинг (design_handoff_personal_rating) ── */

export const ratingLevelSchema = z.object({
  n: z.number(),
  threshold: z.number(),
  title: z.string().nullable().optional(),
});
export type RatingLevel = z.infer<typeof ratingLevelSchema>;

export const ratingHistoryPointSchema = z.object({
  tournamentId: z.number(),
  date: z.string(),
  kind: z.enum(['game', 'decay']),
  place: z.number().nullable(),
  fieldSize: z.number().nullable(),
  delta: z.number(),
  value: z.number(),
});
export type RatingHistoryPoint = z.infer<typeof ratingHistoryPointSchema>;

export const decayStateSchema = z.enum(['none', 'warning', 'active', 'floor', 'returned']);
export type DecayState = z.infer<typeof decayStateSchema>;

export const personalRatingSchema = z.object({
  /** null + gamesPlayed 0 = состояние «нет игр» (RS1). */
  value: z.number().nullable(),
  level: z.number(),
  gamesPlayed: z.number(),
  calibration: z.boolean(),
  lastDelta: z
    .object({ value: z.number(), tournamentId: z.number(), date: z.string() })
    .nullable(),
  peak: z.object({ value: z.number(), date: z.string() }).nullable(),
  seasonPeak: z.object({ value: z.number(), seasonId: z.number() }).nullable(),
  rank: z.number().nullable(),
  leaderboardSize: z.number(),
  trend: z.enum(['up', 'down', 'flat']),
  decayActive: z.boolean(),
  /** Только у владельца; у гостя null (R2-4 — без текстов декея). */
  decay: z
    .object({
      state: decayStateSchema,
      missedInRow: z.number(),
      lostTotal: z.number(),
      nextGame: z
        .object({ id: z.number(), title: z.string().nullable(), startTime: z.string() })
        .nullable(),
    })
    .nullable(),
  history: z.array(ratingHistoryPointSchema),
  levels: z.array(ratingLevelSchema),
});
export type PersonalRating = z.infer<typeof personalRatingSchema>;

export const personalLeaderboardRowSchema = z.object({
  rank: z.number(),
  playerId: z.number(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  level: z.number(),
  value: z.number(),
  trend: z.enum(['up', 'down', 'flat']),
  decayActive: z.boolean(),
  isMe: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
});
export type PersonalLeaderboardRow = z.infer<typeof personalLeaderboardRowSchema>;

export const personalLeaderboardSchema = z.object({
  rows: z.array(personalLeaderboardRowSchema),
  me: personalLeaderboardRowSchema.nullable(),
  meGamesPlayed: z.number(),
  calibrationGames: z.number(),
});
export type PersonalLeaderboard = z.infer<typeof personalLeaderboardSchema>;

export const ratingCelebrationSchema = z
  .object({
    type: z.literal('level_up'),
    level: z.number(),
    value: z.number(),
    threshold: z.number(),
    title: z.string().nullable(),
  })
  .nullable();
export type RatingCelebration = z.infer<typeof ratingCelebrationSchema>;

/* ── Отбор на Чемпионат (design_handoff_qualification) ── */

export const qualStateSchema = z.enum(['calibrating', 'ranked', 'warning', 'decaying', 'at_floor']);
export type QualState = z.infer<typeof qualStateSchema>;

export const qualConfigSchema = z.object({
  id: z.number(),
  name: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  weeks: z.number(),
  plannedGames: z.number(),
  tickets: z.number(),
  calGames: z.number(),
  bonus: z.number(),
  softFrom: z.number(),
  softStep: z.number(),
  hardFrom: z.number(),
  hardStep: z.number(),
  startRating: z.number(),
  floorRating: z.number(),
});
export type QualConfig = z.infer<typeof qualConfigSchema>;

export const qualRankedRowSchema = z.object({
  rank: z.number(),
  rankChangeWeek: z.number().nullable(),
  playerId: z.number(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  gamesPlayed: z.number(),
  state: z.string(),
  rating: z.number(),
  lastGameDelta: z.number().nullable(),
  isMe: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
  /** ❄️ активная заморозка (вечная или неделя) прямо сейчас. */
  frozen: z.boolean().optional(),
  /** Владелец вечной заморозки сезона — шестигранный значок (6.9). */
  eternal: z.boolean().optional(),
});
export type QualRankedRow = z.infer<typeof qualRankedRowSchema>;

export const qualTicketSchema = qualRankedRowSchema.extend({
  weekDelta: z.number(),
  gapToNext: z.number().nullable(),
});

export const qualCalibratingRowSchema = z.object({
  playerId: z.number(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  gamesPlayed: z.number(),
  calGames: z.number(),
  rankIfQualified: z.number().nullable(),
  rating: z.number(),
  isMe: z.boolean(),
  cosmetics: cosmeticsSchema.optional(),
  frozen: z.boolean().optional(),
  eternal: z.boolean().optional(),
});

export const qualMeSchema = z.object({
  playerId: z.number(),
  rank: z.number().nullable(),
  rating: z.number(),
  state: z.string(),
  gamesPlayed: z.number(),
  calGamesLeft: z.number(),
  rankIfQualified: z.number().nullable(),
  weekDelta: z.number(),
  rankChangeWeek: z.number().nullable(),
  gapToTop3: z.number().nullable(),
  lastDelta: z.number().nullable(),
  frozen: z.boolean().optional(),
});

export const qualificationOverviewSchema = z.object({
  status: z.enum(['active', 'upcoming', 'finished', 'none']),
  qualification: qualConfigSchema.optional(),
  progress: z
    .object({
      weekNo: z.number(),
      weeksTotal: z.number(),
      gamesPlayedTotal: z.number(),
      plannedGames: z.number(),
      daysLeft: z.number(),
      daysToStart: z.number(),
      finalWeek: z.boolean(),
      remainingGames: z.array(z.object({ id: z.number(), startsAt: z.string() })),
    })
    .optional(),
  nextTournament: tournamentSchema.nullable().optional(),
  me: qualMeSchema.nullable().optional(),
  tickets: z.array(qualTicketSchema).optional(),
  ranked: z.array(qualRankedRowSchema).optional(),
  calibrating: z.array(qualCalibratingRowSchema).optional(),
  frozenAt: z.string().nullable().optional(),
});
export type QualificationOverview = z.infer<typeof qualificationOverviewSchema>;

export const qualChartPointSchema = z.object({
  gameId: z.number(),
  kind: z.enum(['game', 'miss']),
  date: z.string(),
  value: z.number(),
  delta: z.number(),
  place: z.number().nullable(),
  fieldSize: z.number().nullable(),
});
export type QualChartPoint = z.infer<typeof qualChartPointSchema>;

export const qualHistoryRowSchema = z.object({
  gameId: z.number(),
  kind: z.enum(['game', 'miss']),
  date: z.string(),
  place: z.number().nullable(),
  fieldSize: z.number().nullable(),
  delta: z.number(),
  frozen: z.boolean().optional(),
  breakdown: z
    .object({
      resultShown: z.number(),
      bonus: z.number(),
      floorShown: z.number(),
      total: z.number(),
      playersBeaten: z.number().nullable(),
      beatRaceLeader: z.boolean(),
    })
    .optional(),
});

export const qualPositionSchema = z.object({
  status: z.enum(['active', 'upcoming', 'finished', 'none']),
  qualification: qualConfigSchema.optional(),
  gamesAvailable: z.number().optional(),
  player: z
    .object({
      playerId: z.number(),
      name: z.string(),
      avatarUrl: z.string().nullable(),
      rank: z.number().nullable(),
      rankIfQualified: z.number().nullable(),
      rating: z.number(),
      state: z.string(),
      gamesPlayed: z.number(),
      calGames: z.number(),
      missedInRow: z.number(),
      bestPlace: z.number().nullable(),
      playersBeatenTotal: z.number(),
      participationTotal: z.number(),
      missedTotal: z.number(),
      sinceStart: z.number(),
      weekDelta: z.number(),
      rankChangeWeek: z.number().nullable(),
      gapToTop3: z.number().nullable(),
      cutoffRating: z.number().nullable(),
    })
    .nullable()
    .optional(),
  scaleNeighbors: z
    .array(z.object({ playerId: z.number(), rank: z.number(), rating: z.number(), isMe: z.boolean() }))
    .optional(),
  chart: z.array(qualChartPointSchema).optional(),
  history: z.array(qualHistoryRowSchema).optional(),
  nextTournament: tournamentSchema.nullable().optional(),
  headToHead: z
    .object({
      commonGames: z.number(),
      myWins: z.number(),
      theirWins: z.number(),
      myRating: z.number().nullable(),
      myRank: z.number().nullable(),
    })
    .optional(),
  /** Q2: плитка «Ближайшее достижение» (hint собирает бэкенд). */
  nearestAchievement: z
    .object({
      achievementId: z.number(),
      code: z.string(),
      name: z.string(),
      icon: z.string(),
      level: z.number(),
      maxLevel: z.number(),
      progress: z.number(),
      nextTarget: z.number(),
      hint: z.string(),
    })
    .nullable()
    .optional(),
  /** Профиль Б: 3 последних достижения отбора + счётчик. */
  selectionAchievements: z
    .object({
      categoryId: z.number().nullable(),
      earned: z.number(),
      total: z.number(),
      recent: z.array(
        z.object({
          achievementId: z.number(),
          code: z.string(),
          name: z.string(),
          icon: z.string(),
          level: z.number(),
          maxLevel: z.number(),
          earnedAt: z.string().nullable(),
        }),
      ),
    })
    .optional(),
});
export type QualPosition = z.infer<typeof qualPositionSchema>;

export const qualCelebrationSchema = z
  .object({
    gameId: z.number(),
    gameDate: z.string(),
    place: z.number().nullable(),
    fieldSize: z.number().nullable(),
    playersBeaten: z.number(),
    breakdown: z.object({
      resultShown: z.number(),
      bonus: z.number(),
      floorShown: z.number(),
      total: z.number(),
      shownBefore: z.number(),
      shownAfter: z.number(),
    }),
    rankBefore: z.number().nullable(),
    rankAfter: z.number().nullable(),
    neighbors: z.array(
      z.object({ rank: z.number(), name: z.string(), rating: z.number(), isMe: z.boolean() }),
    ),
    gapBefore: z.number().nullable(),
    gapAfter: z.number().nullable(),
    tickets: z.number(),
    flags: z.object({
      enteredZone: z.boolean(),
      calibrationDone: z.boolean(),
      beatRaceLeader: z.boolean(),
      atFloor: z.boolean(),
      negativeResult: z.boolean(),
      calibrating: z.boolean(),
      calibrationProgress: z.number(),
      calGames: z.number(),
    }),
    nextTournament: tournamentSchema.nullable(),
  })
  .nullable();
export type QualCelebration = z.infer<typeof qualCelebrationSchema>;

export const qualArchiveSchema = z.array(
  z.object({
    id: z.number(),
    name: z.string(),
    endedAt: z.string(),
    winners: z.array(z.object({ rank: z.number(), name: z.string(), rating: z.number() })),
    myResult: z
      .object({ rank: z.number().nullable(), rating: z.number(), gamesPlayed: z.number() })
      .nullable(),
  }),
);
export type QualArchive = z.infer<typeof qualArchiveSchema>;

/* ── Straddle Pass (хэндофф «Straddle Pass — общий пул») ── */

export const passItemSchema = z.object({
  id: z.number(),
  code: z.string(),
  slot: z.enum(['frame', 'nick', 'row', 'bg']),
  name: z.string(),
  rarity: z.number(),
  kind: z.string(),
  asset: z.string(),
});
export type PassItem = z.infer<typeof passItemSchema>;

export const passCellSchema = z.object({
  track: z.enum(['free', 'plus']),
  level: z.number(),
  kind: z.enum(['item', 'deck', 'voucher']),
  state: z.enum(['closed', 'claimable', 'claimed', 'locked']),
  deckRarity: z.number().nullable(),
  voucherType: z.string().nullable(),
  meta: z.string().nullable(),
  item: passItemSchema.omit({ id: true }).nullable(),
});
export type PassCell = z.infer<typeof passCellSchema>;

/** Задание пасса (у всех; выполненные засчитываются сразу, награда — XP).
 *  Игровые привязаны к игре (gameId/difficulty), недельные — к неделе. */
export const passQuestSchema = z.object({
  id: z.number(),
  title: z.string(),
  value: z.number(),
  target: z.number(),
  reward: z.number(),
  done: z.boolean(),
  difficulty: z.enum(['easy', 'medium', 'hard']).nullish(),
  status: z.string().optional(),
  gameId: z.number().nullish(),
  gameStartAt: z.string().nullish(),
});
export type PassQuest = z.infer<typeof passQuestSchema>;

/** 6.1d — экран задания: снапшот + журнал вкладов по играм. */
export const passQuestDetailSchema = passQuestSchema.extend({
  period: z.string(),
  metric: z.string(),
  weekResetAt: z.string().nullable(),
  registeredGameAt: z.string().nullable(),
  history: z.array(z.object({ date: z.string(), delta: z.number(), gameId: z.number() })),
  /** Тексты из CRM; null — шаблоны приложения по метрике. */
  about: z.string().nullish(),
  steps: z.array(z.object({ title: z.string(), text: z.string() })).nullish(),
  conditions: z.array(z.object({ ok: z.boolean(), text: z.string() })).nullish(),
});
export type PassQuestDetail = z.infer<typeof passQuestDetailSchema>;

export const passQuestsSchema = z.object({
  plus: z.boolean(),
  game: z.array(passQuestSchema),
  week: z.array(passQuestSchema),
  weekResetAt: z.string(),
  completed: z.number(),
  total: z.number(),
});
export type PassQuests = z.infer<typeof passQuestsSchema>;

export const passOverviewSchema = z.object({
  status: z.enum(['none', 'active']),
  season: z
    .object({
      id: z.number(),
      name: z.string(),
      startsAt: z.string(),
      endsAt: z.string(),
      pricePlus: z.number(),
      /** Цена Pass+ в ⭐; null — stars-оплата Pass+ выключена. */
      pricePlusStars: z.number().nullish(),
      levels: z.number(),
      xpPerGame: z.number(),
      /** XP на уровень (100); отсутствует у старого API. */
      xpPerLevel: z.number().optional(),
      daysLeft: z.number(),
    })
    .optional(),
  me: z
    .object({
      tier: z.enum(['free', 'plus']),
      level: z.number(),
      xp: z.number(),
      xpFrac: z.number(),
      claimable: z.number(),
      decksUnopened: z.number(),
      guaranteeIn: z.number(),
      plusCelebration: z.boolean(),
      /** Тост «Уровень N! Награда ждёт» при входе в 6.1; null — показан. */
      levelToast: z.number().nullable().optional(),
      lastGameAt: z.string().nullable().optional(),
    })
    .optional(),
  tracks: z
    .array(
      z.object({
        level: z.number(),
        free: passCellSchema.nullable(),
        plus: passCellSchema.nullable(),
      }),
    )
    .optional(),
  quests: passQuestsSchema.optional(),
});
export type PassOverview = z.infer<typeof passOverviewSchema>;

export const passDecksSchema = z.object({
  status: z.enum(['none', 'active']),
  decks: z.array(
    z.object({
      id: z.number(),
      rarity: z.number(),
      source: z.string(),
      obtainedAt: z.string(),
    }),
  ),
});
export type PassDecks = z.infer<typeof passDecksSchema>;

export const passOpenResultSchema = z.object({
  deckId: z.number(),
  rarity: z.number(),
  resultKind: z.enum(['item', 'dup', 'freeze']),
  item: passItemSchema.nullable(),
  freezeKind: z.string().nullable(),
  xpBonus: z.number(),
  alreadyOpened: z.boolean(),
});
export type PassOpenResult = z.infer<typeof passOpenResultSchema>;

export const passChancesSchema = z.object({
  tier: z.string(),
  guaranteeIn: z.number().optional(),
  freezesEnabled: z.boolean(),
  chances: z.object({
    deckByTier: z.object({ free: z.array(z.number()), plus: z.array(z.number()) }),
    itemByDeck: z.record(z.string(), z.array(z.number())),
    freezeGame: z.record(z.string(), z.record(z.string(), z.number())),
    freezeBought: z.record(z.string(), z.record(z.string(), z.number())),
  }),
});
export type PassChances = z.infer<typeof passChancesSchema>;

const shopEntrySchema = z.object({
  price: z.number(),
  bulkQty: z.number().optional(),
  bulkPrice: z.number().optional(),
  /** Цены в Telegram Stars; нет starsPrice — ⭐-оплата позиции недоступна. */
  starsPrice: z.number().optional(),
  starsBulkPrice: z.number().optional(),
});
export type ShopEntry = z.infer<typeof shopEntrySchema>;
export const passShopSchema = z.object({
  shop: z.record(z.string(), shopEntrySchema),
  maxOrderQty: z.number(),
  previews: z.record(z.string(), z.array(passItemSchema)),
});
export type PassShop = z.infer<typeof passShopSchema>;

export const passPrizesSchema = z.object({
  deckRarity: z.number(),
  freezePct: z.number().optional(),
  groups: z.array(
    z.object({ rarity: z.number(), pct: z.number(), items: z.array(passItemSchema) }),
  ),
});
export type PassPrizes = z.infer<typeof passPrizesSchema>;

export const passWardrobeSchema = z.object({
  equipped: z.object({
    frame: z.number().nullable(),
    nick: z.number().nullable(),
    row: z.number().nullable(),
    bg: z.number().nullable(),
  }),
  items: z.array(
    passItemSchema.extend({
      owned: z.boolean(),
      isNew: z.boolean(),
      obtainedAt: z.string().nullable(),
      from: z.object({ track: z.string(), level: z.number() }).nullable(),
    }),
  ),
});
export type PassWardrobe = z.infer<typeof passWardrobeSchema>;

export const passVoucherSchema = z.object({
  type: z.string(),
  level: z.number().nullable(),
  code: z.string(),
  status: z.string(),
  obtainedAt: z.string(),
  redeemedAt: z.string().nullable(),
});
export type PassVoucher = z.infer<typeof passVoucherSchema>;

/** Ответ POST /pass/orders — сырая строка БД (snake_case); нам нужны код и
 *  provider (мог вернуться существующий pending с другим способом оплаты). */
export const passOrderRawSchema = z
  .object({ code: z.string(), provider: z.string().catch('desk') })
  .passthrough();
export type PassOrderRaw = z.infer<typeof passOrderRawSchema>;

export const passOrderSchema = z.object({
  code: z.string(),
  kind: z.string(),
  deckRarity: z.number().nullable(),
  qty: z.number(),
  price: z.number(),
  status: z.string(),
  expiresAt: z.string(),
  provider: z.string().catch('desk'),
  starsPrice: z.number().nullable().optional(),
});
export type PassOrder = z.infer<typeof passOrderSchema>;


export const passFreezesSchema = z.object({
  status: z.enum(['none', 'active']),
  freezesEnabled: z.boolean().optional(),
  auto: z.boolean().optional(),
  balances: z
    .object({ game1: z.number(), game3: z.number(), week: z.number() })
    .optional(),
  eternal: z.boolean().optional(),
  weekActiveUntil: z.string().nullable().optional(),
  history: z
    .array(
      z.object({
        type: z.string(),
        kind: z.string(),
        at: z.string(),
        source: z.string().nullable(),
        deckRarity: z.number().nullable(),
        gameAt: z.string().nullable(),
      }),
    )
    .optional(),
  savedToast: z
    .object({ count: z.number(), lastGameAt: z.string().nullable(), left: z.number() })
    .nullable()
    .optional(),
});
export type PassFreezes = z.infer<typeof passFreezesSchema>;

export const seasonRecapSchema = z.object({
  recap: z
    .object({
      seasonId: z.number(),
      name: z.string(),
      endedAt: z.string(),
      autoOpened: z.number(),
      level: z.number().nullable().optional(),
      wardrobeCount: z.number().optional(),
      drops: z.array(
        z.object({
          deckRarity: z.number(),
          autoOpened: z.boolean(),
          resultKind: z.string(),
          freezeKind: z.string().nullable(),
          item: passItemSchema.omit({ id: true }).nullable(),
        }),
      ),
      burnedFreezes: z.array(z.object({ kind: z.string(), count: z.number() })),
    })
    .nullable(),
});
export type SeasonRecap = z.infer<typeof seasonRecapSchema>;

/* ── Пул-отбор «Калининград» (design_handoff_kaliningrad_v2) ── */

export const kgdStatusSchema = z.enum(['none', 'upcoming', 'running', 'counting', 'finished']);
export type KgdStatus = z.infer<typeof kgdStatusSchema>;

export const kgdEventSchema = z.object({
  id: z.number(),
  name: z.string(),
  city: z.string(),
  tourLabel: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  gamesPlayed: z.number(),
  gamesTotal: z.number(),
  playersCount: z.number(),
  seats: z.number(),
  bestCount: z.number(),
});
export type KgdEvent = z.infer<typeof kgdEventSchema>;

/** Моё состояние: hasPlayed=false — только bestCount (пустая шапка). */
export const kgdMeSchema = z.object({
  hasPlayed: z.boolean(),
  bestCount: z.number(),
  playerId: z.number().optional(),
  position: z.number().optional(),
  total: z.number().optional(),
  bestResults: z.array(z.number()).optional(),
  gamesPlayed: z.number().optional(),
  slotsFilled: z.number().optional(),
  inZone: z.boolean().optional(),
  gapToTop4: z.number().nullable().optional(),
  leadOver5th: z.number().nullable().optional(),
  worstCounted: z.number().nullable().optional(),
  neededPoints: z.number().nullable().optional(),
  neededPlace: z.object({ place: z.number(), field: z.number() }).nullable().optional(),
});
export type KgdMe = z.infer<typeof kgdMeSchema>;

export const kgdRowSchema = z.object({
  position: z.number(),
  playerId: z.number(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  cosmetics: cosmeticsSchema.optional(),
  total: z.number(),
  slotsFilled: z.number(),
  delta24h: z.number().nullable(),
  inZone: z.boolean(),
  isReplacement: z.boolean(),
  tiebreakWon: z.boolean(),
  isMe: z.boolean(),
});
export type KgdRow = z.infer<typeof kgdRowSchema>;

export const kgdOverviewSchema = z.object({
  status: kgdStatusSchema,
  event: kgdEventSchema.optional(),
  me: kgdMeSchema.optional(),
  rows: z.array(kgdRowSchema).optional(),
  nextTournament: tournamentSchema.nullable().optional(),
});
export type KgdOverview = z.infer<typeof kgdOverviewSchema>;

export const kgdSlotSchema = z.object({
  points: z.number(),
  place: z.number().nullable(),
  field: z.number(),
  playedAt: z.string(),
  isWorstCounted: z.boolean(),
});
export type KgdSlot = z.infer<typeof kgdSlotSchema>;

export const kgdGameRowSchema = z.object({
  gameId: z.number(),
  playedAt: z.string(),
  place: z.number().nullable(),
  field: z.number(),
  pool: z.number(),
  points: z.number(),
  counted: z.boolean(),
  corrected: z.boolean(),
});
export type KgdGameRow = z.infer<typeof kgdGameRowSchema>;

export const kgdPositionSchema = z.object({
  status: kgdStatusSchema,
  me: kgdMeSchema.optional(),
  slots: z.array(kgdSlotSchema).optional(),
  games: z.array(kgdGameRowSchema).optional(),
  nextTournament: tournamentSchema.nullable().optional(),
});
export type KgdPosition = z.infer<typeof kgdPositionSchema>;

export const kgdCelebrationSchema = z
  .object({
    gameId: z.number(),
    gameSeq: z.number(),
    gameDate: z.string(),
    field: z.number(),
    place: z.number().nullable(),
    poolBreakdown: z.object({
      entries: z.number(),
      reentries: z.number(),
      pool: z.number(),
      guaranteeApplied: z.boolean(),
    }),
    points: z.number(),
    sharePct: z.number(),
    bestResults: z.array(z.number()),
    bestCount: z.number(),
    outcome: z.union([
      z.object({ kind: z.literal('free_slot'), freeLeft: z.number(), eighthFilled: z.boolean() }),
      z.object({ kind: z.literal('replaced'), replacedPoints: z.number(), sumDelta: z.number() }),
      z.object({ kind: z.literal('not_counted'), worstCounted: z.number() }),
    ]),
    rankBefore: z.number().nullable(),
    rankAfter: z.number(),
    gapToTop4: z.number().nullable(),
    leadOver5th: z.number().nullable(),
    special: z.object({ enteredZone: z.boolean(), won: z.boolean() }),
    nextTournament: tournamentSchema.nullable().optional(),
  })
  .nullable();
export type KgdCelebration = z.infer<typeof kgdCelebrationSchema>;

export const kgdRulesSchema = z.object({
  pointsConfig: z.object({ entry: z.number(), reentry: z.number(), guarantee: z.number() }),
  bestCount: z.number(),
  gamesTotal: z.number(),
  seats: z.number(),
  endsAt: z.string().nullable(),
  city: z.string(),
  tables: z.array(
    z.object({
      field: z.number(),
      pool: z.number(),
      paidPlaces: z.number(),
      rows: z.array(z.object({ place: z.number(), points: z.number() })),
    }),
  ),
});
export type KgdRules = z.infer<typeof kgdRulesSchema>;
